<template>
  <div class="reviews-page">
    <div class="reviews-page__container">
      <nav class="reviews-page__breadcrumbs" aria-label="Хлебные крошки">
        <ol>
          <li>
            <router-link to="/">Главная</router-link>
            <img src="@/assets/svg/arrow-mini.svg" alt="" />
          </li>
          <li>
            <router-link to="/about">О центре</router-link>
            <img src="@/assets/svg/arrow-mini.svg" alt="" />
          </li>
          <li>
            <span aria-current="page">Отзывы</span>
          </li>
        </ol>
      </nav>

      <section class="reviews-hero">
        <div class="reviews-hero__inner">
          <div class="reviews-hero__content">
            <div class="reviews-hero__text">
              <h1 class="reviews-hero__title">Отзывы пациентов</h1>
              <p class="reviews-hero__lead">
                Нам важно ваше мнение. Благодарим за отзывы — они помогают нам становиться лучше
                и делать помощь семьям ещё внимательнее.
              </p>
            </div>
            <router-link to="/about/reviews/leave" class="reviews-hero__cta">
              Оставить отзыв
            </router-link>
          </div>
          <div class="reviews-hero__image" aria-hidden="true">
            <img
              class="reviews-hero__image-img"
              src="@/assets/img/reviews/reviews-hero.png"
              alt=""
            />
          </div>
        </div>
      </section>

      <section class="reviews-nok" aria-labelledby="reviews-nok-title">
        <h2 id="reviews-nok-title" class="reviews-nok__title">Независимая оценка качества</h2>
        <p class="reviews-nok__text">
          Пройдите официальную анкету независимой оценки качества условий оказания услуг
          медицинскими организациями на портале Минздрава России.
        </p>
        <a
          href="https://anketa.rosminzdrav.ru/"
          target="_blank"
          rel="noopener noreferrer"
          class="reviews-nok__link index-btn"
        >Перейти к анкете НОК</a>
      </section>

      <section class="reviews-how" aria-labelledby="reviews-how-title">
        <h2 id="reviews-how-title" class="reviews-how__title">Как оставить отзыв?</h2>
        <ol class="reviews-how__steps">
          <li class="reviews-how__step">
            <span class="reviews-how__icon" aria-hidden="true">
              <img src="@/assets/img/reviews/how-step-1.png" alt="">
            </span>
            <p>Нажмите кнопку «Оставить отзыв»</p>
          </li>
          <li class="reviews-how__step">
            <span class="reviews-how__icon" aria-hidden="true">
              <img src="@/assets/img/reviews/how-step-2.png" alt="">
            </span>
            <p>Заполните форму и оцените работу центра</p>
          </li>
          <li class="reviews-how__step">
            <span class="reviews-how__icon" aria-hidden="true">
              <img src="@/assets/img/reviews/how-step-3.png" alt="">
            </span>
            <p>Отзыв будет опубликован после модерации</p>
          </li>
        </ol>
      </section>

      <section class="reviews-feed" aria-labelledby="reviews-feed-title">
        <div class="reviews-feed__toolbar">
          <h2 id="reviews-feed-title" class="reviews-feed__heading">Все отзывы</h2>
          <label class="reviews-feed__sort">
            <span class="visually-hidden">Сортировка</span>
            <select v-model="sortOrder">
              <option value="new">Сначала новые</option>
              <option value="old">Сначала старые</option>
            </select>
          </label>
        </div>

        <div class="reviews-feed__selects" role="group" aria-label="Фильтры отзывов">
          <ReviewSearchSelect
            label="Отделение"
            placeholder="Все отделения"
            :options="departmentOptions"
            :value-id="filterDepartmentId"
            :value-name="filterDepartmentName"
            @input="onFilterDepartment"
          />
          <ReviewSearchSelect
            label="Врач"
            placeholder="Все врачи"
            :options="employeeOptions"
            :value-id="filterEmployeeId"
            :value-name="filterEmployeeName"
            @input="onFilterEmployee"
          />
          <div class="reviews-feed__theme">
            <label class="reviews-feed__theme-label" for="reviews-theme-filter">Тема</label>
            <select id="reviews-theme-filter" v-model="selectedCategory" class="reviews-feed__theme-select">
              <option value="all">Все темы</option>
              <option v-for="category in categoryOptions" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
          </div>
        </div>

        <p v-if="loading" class="reviews-feed__empty">Загрузка отзывов…</p>
        <p v-else-if="!filteredReviews.length" class="reviews-feed__empty">
          По выбранным фильтрам отзывов пока нет.
        </p>

        <div v-if="featuredReview" class="reviews-feed__featured">
          <ReviewsCard :item="featuredReview" featured />
        </div>

        <div class="reviews-feed__grid">
          <ReviewsCard
            v-for="review in gridReviews"
            :key="review.id"
            :item="review"
          />
        </div>

        <div v-if="canShowMore" class="reviews-feed__more-wrap">
          <button type="button" class="reviews-feed__more-btn" @click="showMore">
            Показать ещё отзывы
          </button>
        </div>
      </section>

      <ReviewsFaq :items="faqItems" />
    </div>
  </div>
</template>

<script>
import ReviewsCard from '@/components/Reviews/ReviewsCard.vue'
import ReviewsFaq from '@/components/Reviews/ReviewsFaq.vue'
import ReviewSearchSelect from '@/components/Reviews/ReviewSearchSelect.vue'
import reviewsApi from '@/api/reviews'
import { REVIEW_FAQ } from '@/data/demoReviews'

const PAGE_STEP = 5
const CATEGORY_OPTIONS = [
  'О родах',
  'О беременности',
  'О неонатологии',
  'О врачах',
  'Поликлиника',
  'Гинекология',
  'Средний мед. персонал',
  'Условия пребывания и сервис',
]

export default {
  name: 'AppReviews',
  components: { ReviewsCard, ReviewsFaq, ReviewSearchSelect },
  metaInfo() {
    return {
      title: 'Отзывы | Алтайский краевой клинический перинатальный центр ДАР',
      meta: [
        {
          name: 'description',
          content:
            'Отзывы пациентов Алтайского краевого клинического перинатального центра ДАР.',
        },
        {
          name: 'keywords',
          content: 'отзывы, перинатальный центр, дар',
        },
      ],
    }
  },
  data() {
    return {
      sortOrder: 'new',
      selectedCategory: 'all',
      filterDepartmentId: '',
      filterDepartmentName: '',
      filterMedicalDepartmentId: '',
      filterEmployeeId: '',
      filterEmployeeName: '',
      departmentOptions: [],
      employeeOptions: [],
      visibleCount: PAGE_STEP,
      faqItems: REVIEW_FAQ,
      categoryOptions: CATEGORY_OPTIONS,
      sourceReviews: [],
      loading: true,
    }
  },
  computed: {
    filteredReviews() {
      return this.sourceReviews.filter((review) => {
        if (this.filterDepartmentId && review.departmentId !== this.filterDepartmentId) {
          return false
        }
        if (this.filterEmployeeId && review.employeeId !== this.filterEmployeeId) {
          return false
        }
        if (this.selectedCategory !== 'all') {
          if (!Array.isArray(review.categories) || !review.categories.includes(this.selectedCategory)) {
            return false
          }
        }
        return true
      })
    },
    sortedReviews() {
      const list = [...this.filteredReviews]
      list.sort((a, b) => {
        const da = new Date(a.date).getTime()
        const db = new Date(b.date).getTime()
        return this.sortOrder === 'new' ? db - da : da - db
      })
      return list
    },
    visibleReviews() {
      return this.sortedReviews.slice(0, this.visibleCount)
    },
    featuredReview() {
      return this.visibleReviews[0] || null
    },
    gridReviews() {
      return this.visibleReviews.slice(1)
    },
    canShowMore() {
      return this.visibleCount < this.sortedReviews.length
    },
  },
  watch: {
    sortOrder() {
      this.visibleCount = PAGE_STEP
    },
    selectedCategory() {
      this.visibleCount = PAGE_STEP
    },
    filterDepartmentId() {
      this.visibleCount = PAGE_STEP
    },
    filterEmployeeId() {
      this.visibleCount = PAGE_STEP
    },
    '$route.query': {
      handler() {
        this.applyQueryFilters()
        this.loadReviews()
      },
      deep: true,
    },
  },
  async mounted() {
    await this.loadRefs()
    this.applyQueryFilters()
    await this.loadReviews()
  },
  methods: {
    unwrapList(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.result)) return data.result
      return []
    },
    async loadRefs() {
      try {
        const [deps, emps] = await Promise.all([
          reviewsApi.getReviewDepartments(),
          reviewsApi.getReviewEmployees(''),
        ])
        this.departmentOptions = this.unwrapList(deps)
        this.employeeOptions = this.unwrapList(emps)
      } catch (e) {
        console.warn(e)
      }
    },
    async loadReviews() {
      this.loading = true
      try {
        const params = {}
        if (this.filterMedicalDepartmentId) {
          params.medicalDepartmentIds = this.filterMedicalDepartmentId
        }
        const response = await reviewsApi.getReviews(params)
        this.sourceReviews = this.unwrapList(response)
      } catch (e) {
        console.warn(e)
        this.sourceReviews = []
      } finally {
        this.loading = false
      }
    },
    applyQueryFilters() {
      const q = (this.$route && this.$route.query) || {}
      const deptId = String(q.departmentId || '').trim()
      const medId = String(q.medicalDepartmentId || '').trim()
      const empId = String(q.employeeId || '').trim()
      this.filterDepartmentId = deptId
      this.filterDepartmentName = ''
      this.filterMedicalDepartmentId = medId
      this.filterEmployeeId = empId
      this.filterEmployeeName = ''
      if (deptId) {
        const found = this.departmentOptions.find((o) => o.id === deptId)
        this.filterDepartmentName = found ? found.name : String(q.departmentName || '')
      }
      if (empId) {
        const found = this.employeeOptions.find((o) => o.id === empId)
        this.filterEmployeeName = found ? found.name : String(q.employeeName || '')
      }
      if (q.category) {
        this.selectedCategory = String(q.category)
      }
    },
    onFilterDepartment({ id, name }) {
      this.filterDepartmentId = id || ''
      this.filterDepartmentName = name || ''
    },
    onFilterEmployee({ id, name }) {
      this.filterEmployeeId = id || ''
      this.filterEmployeeName = name || ''
    },
    showMore() {
      this.visibleCount = Math.min(
        this.visibleCount + PAGE_STEP,
        this.sortedReviews.length
      )
    },
  },
}
</script>
